import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { API, jsonAuthHeaders } from "../services/config";
import "./HistoriaClinicaForm.css";
import SignatureCanvas from "react-signature-canvas";
import HistoriaClinicaImpresion from "../components/HistoriaClinicaImpresion";

const TAB_CONFIG = [
  {
    id: 1,
    label: "Datos",
    shortLabel: "Datos",
    fields: [
      "lugar", "fecha", "odontologo", "matricula", "paciente", "obraSocial", "fechaNac", "tel", "edad",
      "estadoCivil", "nacionalidad", "dni", "cel", "domicilio", "profesion", "titular", "trabajo", "jerarquia",
    ],
  },
  {
    id: 2,
    label: "Antecedentes",
    shortLabel: "Antec.",
    fields: [
      "padreVive", "enfPadre", "madreVive", "enfMadre", "hermanos", "hermanosSanos", "enfermedad",
      "enfermedadDetalle", "tratamientoMedico", "tratamientoDetalle", "medicamentosHabituales",
      "medicamentosUltimos5", "deporte", "malestarDeporte", "alergico", "alergiasDetalle", "cicatrizacion",
      "colageno", "fiebreReumatica", "medicacionProteccion", "diabetico", "controlDiabetes", "problemaCardiaco",
      "cualCardiaco", "aspirinaAnticoagulante", "frecuenciaAnticoagulante", "presionAlta", "chagas",
      "tratamientoChagas", "problemaRenal", "ulcera", "hepatitis", "tipoHepatitis", "problemaHepatico",
      "cualHepatico", "convulsiones", "epilepsia", "medicacionEpilepsia", "sifilis", "infectoContagiosa",
      "transfusiones", "operado", "deQueOperacion", "cuandoOperacion", "problemaRespiratorio", "cualRespiratorio",
      "fuma", "embarazada", "mesesEmbarazo", "otrasEnfermedades", "tratamientosAlternativos", "medicoClinico",
      "clinicaDerivacion",
    ],
  },
  {
    id: 3,
    label: "Odontológica",
    shortLabel: "Odonto.",
    fields: [
      "motivoConsulta", "otroProfesional", "tomoMedicamento", "medicamentosConsulta", "desdeCuando", "resultados",
      "tipoDolor", "localizadoDonde", "irradiadoHacia", "calmaCon", "golpeDientes", "cuandoGolpe", "comoGolpe",
      "fracturaDiente", "cualDiente", "tratamientoFractura", "dificultadHablar", "dificultadMasticar",
      "dificultadAbrirBoca", "dificultadTragar", "manchas", "abultamientos", "ulceraciones", "ampollas",
      "otrosLesiones", "sangradoEncías", "cuandoSangrado", "pus", "deDondePus", "movilidadDientes", "dientesAltos",
      "caraHinchada", "tratamientoCara", "azucarDiario", "indicePlaca", "higieneBucal", "sarro",
      "enfermedadPeriodontal",
    ],
  },
  {
    id: 4,
    label: "Diagnóstico",
    shortLabel: "Diag.",
    fields: ["diagnostico", "planTratamiento", "observaciones"],
  },
  {
    id: 5,
    label: "Consentimiento",
    shortLabel: "Firma",
    fields: ["consentimientoNombre", "consentimientoDni", "consentimientoDomicilio", "consentimientoDr", "firmaBase64"],
  },
];

const EMPTY_TAB_STATE = { 1: false, 2: false, 3: false, 4: false, 5: false };

function hasMeaningfulValue(value) {
  if (value == null) return false;
  if (typeof value === "string") return value.trim() !== "";
  return true;
}

function inferSavedTabs(data) {
  return TAB_CONFIG.reduce((acc, tab) => {
    acc[tab.id] = tab.fields.some((field) => hasMeaningfulValue(data[field]));
    return acc;
  }, { ...EMPTY_TAB_STATE });
}

export default function HistoriaClinicaForm({ pacienteId, embedded = false, onSaved }) {
  const { id: routeId } = useParams(); // compatible con ruta vieja
  const id = pacienteId || routeId;
  const navigate = useNavigate();
  const sigCanvas = useRef({});
  const sigCanvasFullscreen = useRef(null);
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savedTabs, setSavedTabs] = useState({ ...EMPTY_TAB_STATE });
  const [dirtyTabs, setDirtyTabs] = useState({ ...EMPTY_TAB_STATE });
  const [saving, setSaving] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState(null);
  const [firmaFullscreen, setFirmaFullscreen] = useState(false);
  const [showImpresion, setShowImpresion] = useState(false);

  // Estado del formulario
  const [formData, setFormData] = useState({
    // === DATOS PERSONALES ===
    lugar: "", fecha: "", odontologo: "", matricula: "",
    paciente: "", obraSocial: "", fechaNac: "", tel: "", edad: "",
    estadoCivil: "", nacionalidad: "", dni: "", cel: "", domicilio: "",
    profesion: "", titular: "", trabajo: "", jerarquia: "",
    // === ANTECEDENTES MÉDICOS ===
    padreVive: "", enfPadre: "", madreVive: "", enfMadre: "",
    hermanos: "", hermanosSanos: "", enfermedad: "", enfermedadDetalle: "",
    tratamientoMedico: "", tratamientoDetalle: "",
    medicamentosHabituales: "", medicamentosUltimos5: "",
    deporte: "", malestarDeporte: "", alergico: "", alergiasDetalle: "",
    cicatrizacion: "", colageno: "", fiebreReumatica: "",
    medicacionProteccion: "", diabetico: "", controlDiabetes: "",
    problemaCardiaco: "", cualCardiaco: "", aspirinaAnticoagulante: "",
    frecuenciaAnticoagulante: "", presionAlta: "", chagas: "",
    tratamientoChagas: "", problemaRenal: "", ulcera: "",
    hepatitis: "", tipoHepatitis: "", problemaHepatico: "",
    cualHepatico: "", convulsiones: "", epilepsia: "", medicacionEpilepsia: "",
    sifilis: "", infectoContagiosa: "", transfusiones: "", operado: "",
    deQueOperacion: "", cuandoOperacion: "", problemaRespiratorio: "",
    cualRespiratorio: "", fuma: "", embarazada: "", mesesEmbarazo: "",
    otrasEnfermedades: "", tratamientosAlternativos: "", medicoClinico: "",
    clinicaDerivacion: "",
    // === HISTORIA ODONTOLÓGICA ===
    motivoConsulta: "", otroProfesional: "", tomoMedicamento: "",
    medicamentosConsulta: "", desdeCuando: "", resultados: "", tipoDolor: "",
    localizadoDonde: "", irradiadoHacia: "", calmaCon: "",
    golpeDientes: "", cuandoGolpe: "", comoGolpe: "",
    fracturaDiente: "", cualDiente: "", tratamientoFractura: "",
    dificultadHablar: "", dificultadMasticar: "", dificultadAbrirBoca: "",
    dificultadTragar: "", manchas: "", abultamientos: "", ulceraciones: "",
    ampollas: "", otrosLesiones: "", sangradoEncías: "", cuandoSangrado: "",
    pus: "", deDondePus: "", movilidadDientes: "", dientesAltos: "",
    caraHinchada: "", tratamientoCara: "", azucarDiario: "", indicePlaca: "",
    higieneBucal: "", sarro: "", enfermedadPeriodontal: "",
    // === DIAGNÓSTICO Y PLAN ===
    diagnostico: "", planTratamiento: "", observaciones: "",
    // === CONSENTIMIENTO ===
    consentimientoNombre: "", consentimientoDni: "",
    consentimientoDomicilio: "", consentimientoDr: "",
    firmaBase64: "" // Para guardar la firma recuperada
  });

  // ==================================================================
  // 1. CARGAR DATOS AL INICIAR
  // ==================================================================
  useEffect(() => {
    const fetchHistoria = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("No estás autenticado");
        navigate("/login");
        return;
      }

      try {
        // Primero cargamos datos básicos del paciente para llenar el encabezado si es nuevo
        const respPaciente = await fetch(`${API}/pacientes/${id}`, {
            headers: jsonAuthHeaders()
        });
        const dataPaciente = await respPaciente.json();
        
        // Luego intentamos cargar la historia clínica existente
        const response = await fetch(`${API}/pacientes/${id}/historia-clinica`, {
          headers: jsonAuthHeaders()
        });
        const result = await response.json();

        if (result.success && result.data) {
          const dbData = result.data;
          
          // Mapeo inverso: DB (snake_case) -> State (camelCase)
          // Esto es necesario porque la DB usa guiones bajos
          setFormData(prev => ({
            ...prev,
            // Datos básicos (rellenamos con datos del paciente si faltan en historia)
            paciente: dataPaciente.data.nombre + " " + dataPaciente.data.apellido,
            dni: dataPaciente.data.dni,
            tel: dataPaciente.data.telefono,
            fechaNac: dbData.fecha_nacimiento || "",
            
            // Resto de campos mapeados manualmente
            lugar: dbData.lugar || "",
            fecha: dbData.fecha_consulta || "",
            odontologo: dbData.odontologo || "",
            matricula: dbData.matricula || "",
            obraSocial: dbData.obra_social || "",
            edad: dbData.edad || "",
            estadoCivil: dbData.estado_civil || "",
            nacionalidad: dbData.nacionalidad || "",
            cel: dbData.celular || "",
            domicilio: dbData.domicilio || "",
            profesion: dbData.profesion || "",
            trabajo: dbData.lugar_trabajo || "",
            jerarquia: dbData.jerarquia || "",
            
            // Antecedentes
            padreVive: dbData.padre_vive || "",
            enfPadre: dbData.enf_padre || "",
            madreVive: dbData.madre_vive || "",
            enfMadre: dbData.enf_madre || "",
            hermanos: dbData.hermanos || "",
            hermanosSanos: dbData.hermanos_sanos || "",
            enfermedad: dbData.enfermedad_personal || "",
            enfermedadDetalle: dbData.enfermedad_detalle || "",
            tratamientoMedico: dbData.tratamiento_medico || "",
            tratamientoDetalle: dbData.tratamiento_detalle || "",
            medicamentosHabituales: dbData.medicamentos_habituales || "",
            medicamentosUltimos5: dbData.medicamentos_ultimos_5 || "",
            deporte: dbData.deporte || "",
            malestarDeporte: dbData.malestar_deporte || "",
            alergico: dbData.alergico || "",
            alergiasDetalle: dbData.alergias_detalle || "",
            cicatrizacion: dbData.cicatrizacion || "",
            colageno: dbData.colageno || "",
            fiebreReumatica: dbData.fiebre_reumatica || "",
            medicacionProteccion: dbData.medicacion_proteccion || "",
            diabetico: dbData.diabetico || "",
            controlDiabetes: dbData.control_diabetes || "",
            problemaCardiaco: dbData.problema_cardiaco || "",
            cualCardiaco: dbData.cual_cardiaco || "",
            aspirinaAnticoagulante: dbData.aspirina_anticoagulante || "",
            frecuenciaAnticoagulante: dbData.frecuencia_anticoagulante || "",
            presionAlta: dbData.presion_alta || "",
            chagas: dbData.chagas || "",
            tratamientoChagas: dbData.tratamiento_chagas || "",
            problemaRenal: dbData.problema_renal || "",
            ulcera: dbData.ulcera || "",
            hepatitis: dbData.hepatitis || "",
            tipoHepatitis: dbData.tipo_hepatitis || "",
            problemaHepatico: dbData.problema_hepatico || "",
            cualHepatico: dbData.cual_hepatico || "",
            convulsiones: dbData.convulsiones || "",
            epilepsia: dbData.epilepsia || "",
            medicacionEpilepsia: dbData.medicacion_epilepsia || "",
            sifilis: dbData.sifilis || "",
            infectoContagiosa: dbData.infecto_contagiosa || "",
            transfusiones: dbData.transfusiones || "",
            operado: dbData.operado || "",
            deQueOperacion: dbData.de_que_operacion || "",
            cuandoOperacion: dbData.cuando_operacion || "",
            problemaRespiratorio: dbData.problema_respiratorio || "",
            cualRespiratorio: dbData.cual_respiratorio || "",
            fuma: dbData.fuma || "",
            embarazada: dbData.embarazada || "",
            mesesEmbarazo: dbData.meses_embarazo || "",
            otrasEnfermedades: dbData.otras_enfermedades || "",
            tratamientosAlternativos: dbData.tratamientos_alternativos || "",
            medicoClinico: dbData.medico_clinico || "",
            clinicaDerivacion: dbData.clinica_derivacion || "",
            
            // Odontología
            motivoConsulta: dbData.motivo_consulta || "",
            otroProfesional: dbData.otro_profesional || "",
            tomoMedicamento: dbData.tomo_medicamento || "",
            medicamentosConsulta: dbData.medicamentos_consulta || "",
            desdeCuando: dbData.desde_cuando || "",
            resultados: dbData.resultados || "",
            tipoDolor: dbData.tipo_dolor || "",
            localizadoDonde: dbData.localizado_donde || "",
            irradiadoHacia: dbData.irradiado_hacia || "",
            calmaCon: dbData.calma_con || "",
            golpeDientes: dbData.golpe_dientes || "",
            cuandoGolpe: dbData.cuando_golpe || "",
            comoGolpe: dbData.como_golpe || "",
            fracturaDiente: dbData.fractura_diente || "",
            cualDiente: dbData.cual_diente || "",
            tratamientoFractura: dbData.tratamiento_fractura || "",
            dificultadHablar: dbData.dificultad_hablar || "",
            dificultadMasticar: dbData.dificultad_masticar || "",
            dificultadAbrirBoca: dbData.dificultad_abrir_boca || "",
            dificultadTragar: dbData.dificultad_tragar || "",
            manchas: dbData.manchas || "",
            abultamientos: dbData.abultamientos || "",
            ulceraciones: dbData.ulceraciones || "",
            ampollas: dbData.ampollas || "",
            otrosLesiones: dbData.otros_lesiones || "",
            sangradoEncías: dbData.sangrado_encias || "",
            cuandoSangrado: dbData.cuando_sangrado || "",
            pus: dbData.pus || "",
            deDondePus: dbData.de_donde_pus || "",
            movilidadDientes: dbData.movilidad_dientes || "",
            dientesAltos: dbData.dientes_altos || "",
            caraHinchada: dbData.cara_hinchada || "",
            tratamientoCara: dbData.tratamiento_cara || "",
            azucarDiario: dbData.azucar_diario || "",
            indicePlaca: dbData.indice_placa || "",
            higieneBucal: dbData.higiene_bucal || "",
            sarro: dbData.sarro || "",
            enfermedadPeriodontal: dbData.enfermedad_periodontal || "",

            // Diagnóstico
            diagnostico: dbData.diagnostico || "",
            planTratamiento: dbData.plan_tratamiento || "",
            observaciones: dbData.observaciones || "",

            // Consentimiento y firma
            consentimientoNombre: dbData.consentimiento_nombre || "",
            consentimientoDni: dbData.consentimiento_dni || "",
            consentimientoDomicilio: dbData.consentimiento_domicilio || "",
            consentimientoDr: dbData.consentimiento_dr || "",
            firmaBase64: dbData.firma_base64 || ""
          }));
          setSavedTabs(inferSavedTabs({
            ...formData,
            lugar: dbData.lugar || "",
            fecha: dbData.fecha_consulta || "",
            odontologo: dbData.odontologo || "",
            matricula: dbData.matricula || "",
            paciente: dataPaciente.data.nombre + " " + dataPaciente.data.apellido,
            obraSocial: dbData.obra_social || "",
            fechaNac: dbData.fecha_nacimiento || "",
            tel: dataPaciente.data.telefono,
            edad: dbData.edad || "",
            estadoCivil: dbData.estado_civil || "",
            nacionalidad: dbData.nacionalidad || "",
            dni: dataPaciente.data.dni,
            cel: dbData.celular || "",
            domicilio: dbData.domicilio || "",
            profesion: dbData.profesion || "",
            titular: dbData.titular || "",
            trabajo: dbData.lugar_trabajo || "",
            jerarquia: dbData.jerarquia || "",
            padreVive: dbData.padre_vive || "",
            enfPadre: dbData.enf_padre || "",
            madreVive: dbData.madre_vive || "",
            enfMadre: dbData.enf_madre || "",
            hermanos: dbData.hermanos || "",
            hermanosSanos: dbData.hermanos_sanos || "",
            enfermedad: dbData.enfermedad_personal || "",
            enfermedadDetalle: dbData.enfermedad_detalle || "",
            tratamientoMedico: dbData.tratamiento_medico || "",
            tratamientoDetalle: dbData.tratamiento_detalle || "",
            medicamentosHabituales: dbData.medicamentos_habituales || "",
            medicamentosUltimos5: dbData.medicamentos_ultimos_5 || "",
            deporte: dbData.deporte || "",
            malestarDeporte: dbData.malestar_deporte || "",
            alergico: dbData.alergico || "",
            alergiasDetalle: dbData.alergias_detalle || "",
            cicatrizacion: dbData.cicatrizacion || "",
            colageno: dbData.colageno || "",
            fiebreReumatica: dbData.fiebre_reumatica || "",
            medicacionProteccion: dbData.medicacion_proteccion || "",
            diabetico: dbData.diabetico || "",
            controlDiabetes: dbData.control_diabetes || "",
            problemaCardiaco: dbData.problema_cardiaco || "",
            cualCardiaco: dbData.cual_cardiaco || "",
            aspirinaAnticoagulante: dbData.aspirina_anticoagulante || "",
            frecuenciaAnticoagulante: dbData.frecuencia_anticoagulante || "",
            presionAlta: dbData.presion_alta || "",
            chagas: dbData.chagas || "",
            tratamientoChagas: dbData.tratamiento_chagas || "",
            problemaRenal: dbData.problema_renal || "",
            ulcera: dbData.ulcera || "",
            hepatitis: dbData.hepatitis || "",
            tipoHepatitis: dbData.tipo_hepatitis || "",
            problemaHepatico: dbData.problema_hepatico || "",
            cualHepatico: dbData.cual_hepatico || "",
            convulsiones: dbData.convulsiones || "",
            epilepsia: dbData.epilepsia || "",
            medicacionEpilepsia: dbData.medicacion_epilepsia || "",
            sifilis: dbData.sifilis || "",
            infectoContagiosa: dbData.infecto_contagiosa || "",
            transfusiones: dbData.transfusiones || "",
            operado: dbData.operado || "",
            deQueOperacion: dbData.de_que_operacion || "",
            cuandoOperacion: dbData.cuando_operacion || "",
            problemaRespiratorio: dbData.problema_respiratorio || "",
            cualRespiratorio: dbData.cual_respiratorio || "",
            fuma: dbData.fuma || "",
            embarazada: dbData.embarazada || "",
            mesesEmbarazo: dbData.meses_embarazo || "",
            otrasEnfermedades: dbData.otras_enfermedades || "",
            tratamientosAlternativos: dbData.tratamientos_alternativos || "",
            medicoClinico: dbData.medico_clinico || "",
            clinicaDerivacion: dbData.clinica_derivacion || "",
            motivoConsulta: dbData.motivo_consulta || "",
            otroProfesional: dbData.otro_profesional || "",
            tomoMedicamento: dbData.tomo_medicamento || "",
            medicamentosConsulta: dbData.medicamentos_consulta || "",
            desdeCuando: dbData.desde_cuando || "",
            resultados: dbData.resultados || "",
            tipoDolor: dbData.tipo_dolor || "",
            localizadoDonde: dbData.localizado_donde || "",
            irradiadoHacia: dbData.irradiado_hacia || "",
            calmaCon: dbData.calma_con || "",
            golpeDientes: dbData.golpe_dientes || "",
            cuandoGolpe: dbData.cuando_golpe || "",
            comoGolpe: dbData.como_golpe || "",
            fracturaDiente: dbData.fractura_diente || "",
            cualDiente: dbData.cual_diente || "",
            tratamientoFractura: dbData.tratamiento_fractura || "",
            dificultadHablar: dbData.dificultad_hablar || "",
            dificultadMasticar: dbData.dificultad_masticar || "",
            dificultadAbrirBoca: dbData.dificultad_abrir_boca || "",
            dificultadTragar: dbData.dificultad_tragar || "",
            manchas: dbData.manchas || "",
            abultamientos: dbData.abultamientos || "",
            ulceraciones: dbData.ulceraciones || "",
            ampollas: dbData.ampollas || "",
            otrosLesiones: dbData.otros_lesiones || "",
            sangradoEncías: dbData.sangrado_encias || "",
            cuandoSangrado: dbData.cuando_sangrado || "",
            pus: dbData.pus || "",
            deDondePus: dbData.de_donde_pus || "",
            movilidadDientes: dbData.movilidad_dientes || "",
            dientesAltos: dbData.dientes_altos || "",
            caraHinchada: dbData.cara_hinchada || "",
            tratamientoCara: dbData.tratamiento_cara || "",
            azucarDiario: dbData.azucar_diario || "",
            indicePlaca: dbData.indice_placa || "",
            higieneBucal: dbData.higiene_bucal || "",
            sarro: dbData.sarro || "",
            enfermedadPeriodontal: dbData.enfermedad_periodontal || "",
            diagnostico: dbData.diagnostico || "",
            planTratamiento: dbData.plan_tratamiento || "",
            observaciones: dbData.observaciones || "",
            consentimientoNombre: dbData.consentimiento_nombre || "",
            consentimientoDni: dbData.consentimiento_dni || "",
            consentimientoDomicilio: dbData.consentimiento_domicilio || "",
            consentimientoDr: dbData.consentimiento_dr || "",
            firmaBase64: dbData.firma_base64 || "",
          }));
        } else {
            // Si es nueva historia, al menos prellenamos nombre del paciente
             setFormData(prev => ({
                ...prev,
                paciente: dataPaciente.data.nombre + " " + dataPaciente.data.apellido,
                dni: dataPaciente.data.dni,
                tel: dataPaciente.data.telefono,
             }));
             setSavedTabs({ ...EMPTY_TAB_STATE });
        }
        setDirtyTabs({ ...EMPTY_TAB_STATE });
        setLoading(false);
      } catch (err) {
        console.error("Error cargando historia:", err);
        setError("Error al cargar los datos.");
        setLoading(false);
      }
    };

    fetchHistoria();
  }, [id, navigate]);

  // ==================================================================
  // 2. RESTAURAR FIRMA AL ENTRAR AL PASO 5
  // ==================================================================
  useEffect(() => {
    // Si estamos en el paso 5 y tenemos una firma guardada y el canvas está listo
    if (step === 5 && formData.firmaBase64 && sigCanvas.current) {
        // Pequeño timeout para asegurar que el canvas se montó
        setTimeout(() => {
            if(sigCanvas.current && typeof sigCanvas.current.fromDataURL === 'function') {
                sigCanvas.current.fromDataURL(formData.firmaBase64);
            }
        }, 100);
    }
  }, [step, formData.firmaBase64]);

  useEffect(() => {
    if (step !== 5) return;
    setFormData((prev) => {
      const updates = {};
      if (!prev.consentimientoNombre?.trim() && prev.paciente?.trim()) {
        updates.consentimientoNombre = prev.paciente.trim();
      }
      if (!prev.consentimientoDni?.trim() && prev.dni?.trim()) {
        updates.consentimientoDni = prev.dni.trim();
      }
      if (!prev.consentimientoDomicilio?.trim() && prev.domicilio?.trim()) {
        updates.consentimientoDomicilio = prev.domicilio.trim();
      }
      if (Object.keys(updates).length === 0) return prev;
      return { ...prev, ...updates };
    });
  }, [step]);

  const openFirmaFullscreen = () => {
    setFirmaFullscreen(true);
    setTimeout(() => {
      const dst = sigCanvasFullscreen.current;
      if (!dst) return;
      dst.clear();
      if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
        dst.fromDataURL(sigCanvas.current.toDataURL("image/png"));
      } else if (formData.firmaBase64) {
        dst.fromDataURL(formData.firmaBase64);
      }
    }, 120);
  };

  const closeFirmaFullscreen = () => setFirmaFullscreen(false);

  useEffect(() => {
    if (!firmaFullscreen) return undefined;
    document.body.classList.add("hc-firma-fullscreen-open");
    return () => document.body.classList.remove("hc-firma-fullscreen-open");
  }, [firmaFullscreen]);

  const clearFirmaFullscreen = () => {
    sigCanvasFullscreen.current?.clear();
  };

  const confirmFirmaFullscreen = () => {
    const canvas = sigCanvasFullscreen.current;
    if (canvas && !canvas.isEmpty()) {
      const dataUrl = canvas.toDataURL("image/png");
      if (sigCanvas.current) {
        sigCanvas.current.clear();
        sigCanvas.current.fromDataURL(dataUrl);
      }
      setFormData((prev) => ({ ...prev, firmaBase64: dataUrl }));
      setDirtyTabs((prev) => ({ ...prev, 5: true }));
      setSaveFeedback(null);
    }
    setFirmaFullscreen(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setDirtyTabs(prev => ({ ...prev, [step]: true }));
    setSaveFeedback(null);
  };

  const nextStep = () => setStep((s) => Math.min(s + 1, 5));
  const prevStep = () => setStep((s) => Math.max(s - 1, 1));

  const exitForm = () => {
    if (window.confirm("¿Seguro que deseas salir? Se perderán los cambios no guardados.")) {
      if (!embedded) {
        navigate("/pacientes");
      }
    }
  };

  // ==================================================================
  // 3. ENVIAR DATOS AL BACKEND
  // ==================================================================
  const persistHistoria = async (finalize = false) => {
    const token = localStorage.getItem("token");
    const currentTab = step;

    // Extraer firma del canvas si existe
    let firmaParaEnviar = formData.firmaBase64;
    
    // Si el canvas está activo y tiene trazos, tomamos la nueva firma
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
firmaParaEnviar = sigCanvas.current.toDataURL("image/png");    }

    const payload = {
        ...formData,
        firma: firmaParaEnviar // Enviamos como 'firma' para que el controller lo reciba
    };

    try {
        setSaving(true);
        const response = await fetch(`${API}/pacientes/${id}/historia-clinica`, {
            method: 'POST',
            headers: jsonAuthHeaders(),
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            throw new Error('Error al guardar en el servidor');
        }

        await response.json();
        setSavedTabs(prev => ({ ...prev, [currentTab]: true }));
        setDirtyTabs(prev => ({ ...prev, [currentTab]: false }));

        const effectiveSavedAfter = TAB_CONFIG.reduce((acc, tab) => {
          const dirty = tab.id === currentTab ? false : dirtyTabs[tab.id];
          const saved = tab.id === currentTab ? true : savedTabs[tab.id];
          acc[tab.id] = saved && !dirty;
          return acc;
        }, {});
        const faltantes = TAB_CONFIG.filter(tab => !effectiveSavedAfter[tab.id]).map(tab => tab.label);

        if (finalize) {
          if (faltantes.length > 0) {
            setSaveFeedback({
              type: "warning",
              text: "Todavía hay pestañas pendientes. Guardalas desde cada sección con 'Guardar pestaña' antes de completar la H.C.",
            });
            return;
          }
          setSaveFeedback({ type: "success", text: "Historia clínica guardada completa ✅" });
          if (typeof onSaved === "function") onSaved();
          if (!embedded) navigate("/pacientes");
          return;
        }

        setSaveFeedback({
          type: "success",
          text: faltantes.length
            ? `Pestaña "${TAB_CONFIG.find(t => t.id === currentTab)?.label}" guardada. Faltan: ${faltantes.join(", ")}.`
            : "Pestaña guardada. Todas las pestañas están al día ✅",
        });

    } catch (error) {
        console.error(error);
        setSaveFeedback({ type: "error", text: "Hubo un error al guardar la historia clínica." });
    } finally {
        setSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await persistHistoria(true);
  };

  const handleSaveCurrentTab = async () => {
    await persistHistoria(false);
  };

  const effectiveSavedTabs = TAB_CONFIG.reduce((acc, tab) => {
    acc[tab.id] = savedTabs[tab.id] && !dirtyTabs[tab.id];
    return acc;
  }, { ...EMPTY_TAB_STATE });

  const tabClassName = (tab) => [
    step === tab.id ? "active" : "",
    effectiveSavedTabs[tab.id] ? "done" : "pending",
    dirtyTabs[tab.id] ? "dirty" : "",
  ].join(" ").trim();

  if (loading) return <div className="loading-container">Cargando Historia Clínica...</div>;
  if (error) return <div className="error-container">{error} <button onClick={() => navigate('/pacientes')}>Volver</button></div>;

  return (
<div className={embedded ? "historia-clinica-container embedded" : "historia-clinica-container"}>
      <div className="historia-clinica-content">
        {!embedded && <h2>Historia Clínica General</h2>}

        <div className="hc-tabs-mobile">
          <p className="hc-step-progress">
            Paso {step} de {TAB_CONFIG.length} · {TAB_CONFIG.find((t) => t.id === step)?.label}
          </p>
          <div className="hc-tabs-grid">
            {TAB_CONFIG.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`hc-tab-btn ${tabClassName(tab)}${tab.id === 5 ? " hc-tab-btn--wide" : ""}`}
                onClick={() => setStep(tab.id)}
              >
                <span className="hc-tab-btn__num">{tab.id}</span>
                <span className="hc-tab-btn__label">{tab.shortLabel}</span>
              </button>
            ))}
          </div>
        </div>

        {saveFeedback && (
          <div className={`save-feedback ${saveFeedback.type}`}>
            {saveFeedback.text}
          </div>
        )}

        <form onSubmit={handleSubmit} className={`historia-clinica-form step-${step}`}>

          {/* === 1. DATOS DEL PACIENTE === */}
          {step === 1 && (
            <>
              <h3>Datos del Paciente</h3> <br />
              <label>Lugar (vive):<input name="lugar" value={formData.lugar} onChange={handleChange} /></label>
              <label>Fecha de Consulta:<input type="date" name="fecha" value={formData.fecha} onChange={handleChange} /></label>
              <label>Odontólogo:<input name="odontologo" value={formData.odontologo} onChange={handleChange} /></label>
              <label>Paciente:<input name="paciente" value={formData.paciente} onChange={handleChange} /></label>
              <label>Obra Social:<input name="obraSocial" value={formData.obraSocial} onChange={handleChange} /></label>
              <label>Fecha de Nacimiento:<input type="date" name="fechaNac" value={formData.fechaNac} onChange={handleChange} /></label>
              <label>Teléfono:<input name="tel" value={formData.tel} onChange={handleChange} /></label>
              <label>Edad:<input name="edad" value={formData.edad} onChange={handleChange} /></label>
              <label>Estado Civil:<input name="estadoCivil" value={formData.estadoCivil} onChange={handleChange} /></label>
              <label>Nacionalidad:<input name="nacionalidad" value={formData.nacionalidad} onChange={handleChange} /></label>
              <label>DNI:<input name="dni" value={formData.dni} onChange={handleChange} /></label>
              <label>Celular:<input name="cel" value={formData.cel} onChange={handleChange} /></label>
              <label>Domicilio:<input name="domicilio" value={formData.domicilio} onChange={handleChange} /></label>
              <label>Profesión/Actividad:<input name="profesion" value={formData.profesion} onChange={handleChange} /></label>
              <label>Lugar de trabajo:<input name="trabajo" value={formData.trabajo} onChange={handleChange} /></label>
              <label>Jerarquía:<input name="jerarquia" value={formData.jerarquia} onChange={handleChange} /></label>
              <label>Titular:<input name="titular" value={formData.titular} onChange={handleChange} /></label>
            </>
          )}

          {/* === 2. ANTECEDENTES MÉDICOS === */}
          {step === 2 && (
            <>
              <h3>Antecedentes Médicos</h3> <br />
              <label>Padre con vida:<select name="padreVive" value={formData.padreVive} onChange={handleChange}><option></option><option>SI</option><option>NO</option></select></label>
              <label>Enfermedad padre:<input name="enfPadre" value={formData.enfPadre} onChange={handleChange} /></label>
              <label>Madre con vida:<select name="madreVive" value={formData.madreVive} onChange={handleChange}><option></option><option>SI</option><option>NO</option></select></label>
              <label>Enfermedad madre:<input name="enfMadre" value={formData.enfMadre} onChange={handleChange} /></label>
              <label>Hermanos:<select name="hermanos" value={formData.hermanos} onChange={handleChange}><option></option><option>SI</option><option>NO</option></select></label>
              <label>¿Sanos?(los hermanos):<input name="hermanosSanos" value={formData.hermanosSanos} onChange={handleChange} /></label>
              <label>¿Sufre alguna enfermedad?(usted):<select name="enfermedad" value={formData.enfermedad} onChange={handleChange}><option></option><option>SI</option><option>NO</option></select></label>
              <label>Detalle Enfermedad:<input name="enfermedadDetalle" value={formData.enfermedadDetalle} onChange={handleChange} /></label>
              <label>Tratamiento médico:<select name="tratamientoMedico" value={formData.tratamientoMedico} onChange={handleChange}><option></option><option>SI</option><option>NO</option></select></label>
              <label>Detalle Tratamiento:<input name="tratamientoDetalle" value={formData.tratamientoDetalle} onChange={handleChange} /></label>
              <label>Medicamentos habituales:<textarea name="medicamentosHabituales" value={formData.medicamentosHabituales} onChange={handleChange}></textarea></label>
              <label>Medicamentos últimos 5 años:<textarea name="medicamentosUltimos5" value={formData.medicamentosUltimos5} onChange={handleChange}></textarea></label>
              <label>Deporte:<select name="deporte" value={formData.deporte} onChange={handleChange}><option></option><option>SI</option><option>NO</option></select></label>
              <label>Malestar al realizarlo:<select name="malestarDeporte" value={formData.malestarDeporte} onChange={handleChange}><option></option><option>SI</option><option>NO</option></select></label>
              <label>Alérgico:<select name="alergico" value={formData.alergico} onChange={handleChange}><option></option><option>SI</option><option>NO</option></select></label>
              <label>Detalle Alergias:<input name="alergiasDetalle" value={formData.alergiasDetalle} onChange={handleChange} /></label>
              {/* Se pueden agregar el resto de campos de antecedentes siguiendo el mismo patrón... */}
              <label>Problema Cardíaco:<select name="problemaCardiaco" value={formData.problemaCardiaco} onChange={handleChange}><option></option><option>SI</option><option>NO</option></select></label>
              <label>Cual:<input name="cualCardiaco" value={formData.cualCardiaco} onChange={handleChange} /></label>
              <label>Diabético:<select name="diabetico" value={formData.diabetico} onChange={handleChange}><option></option><option>SI</option><option>NO</option></select></label>
              <label>Presión Alta:<select name="presionAlta" value={formData.presionAlta} onChange={handleChange}><option></option><option>SI</option><option>NO</option></select></label>
              <label>Fuma:<select name="fuma" value={formData.fuma} onChange={handleChange}><option></option><option>SI</option><option>NO</option></select></label>
              <label>Observaciones/Otras:<textarea name="otrasEnfermedades" value={formData.otrasEnfermedades} onChange={handleChange}></textarea></label>
            </>
          )}

          {/* === 3. HISTORIA ODONTOLÓGICA === */}
          {step === 3 && (
            <>
              <h3>Historia Odontológica</h3> <br />
              <label>Motivo de consulta:<textarea name="motivoConsulta" value={formData.motivoConsulta} onChange={handleChange}></textarea></label>
              <label>Consultó antes con otro profesional:<select name="otroProfesional" value={formData.otroProfesional} onChange={handleChange}><option></option><option>SI</option><option>NO</option></select></label>
              <label>Tomó medicamentos:<select name="tomoMedicamento" value={formData.tomoMedicamento} onChange={handleChange}><option></option><option>SI</option><option>NO</option></select></label>
              <label>Nombre de medicamentos:<textarea name="medicamentosConsulta" value={formData.medicamentosConsulta} onChange={handleChange}></textarea></label>
              <label>Desde cuándo:<input name="desdeCuando" value={formData.desdeCuando} onChange={handleChange} /></label>
              <label>Obtuvo resultados:<select name="resultados" value={formData.resultados} onChange={handleChange}><option></option><option>SI</option><option>NO</option></select></label>
              <label>Tipo de dolor:<input name="tipoDolor" value={formData.tipoDolor} onChange={handleChange} /></label>
              <label>Localizado en (dolor):<input name="localizadoDonde" value={formData.localizadoDonde} onChange={handleChange} /></label>
              <label>Irradiado hacia:<input name="irradiadoHacia" value={formData.irradiadoHacia} onChange={handleChange} /></label>
              <label>¿Puede calmarlo con algo?:<input name="calmaCon" value={formData.calmaCon} onChange={handleChange} /></label>
              <label>Sangrado encías:<select name="sangradoEncías" value={formData.sangradoEncías} onChange={handleChange}><option></option><option>SI</option><option>NO</option></select></label>
            </>
          )}

          {/* === 4. DIAGNÓSTICO Y PLAN === */}
          {step === 4 && (
            <>
              <h3>Diagnóstico y Plan</h3> <br />
              <label>Diagnóstico presuntivo:<textarea name="diagnostico" value={formData.diagnostico} onChange={handleChange}></textarea></label>
              <label>Plan de tratamiento:<textarea name="planTratamiento" value={formData.planTratamiento} onChange={handleChange}></textarea></label>
              <label>Observaciones:<textarea name="observaciones" value={formData.observaciones} onChange={handleChange}></textarea></label>
            </>
          )}

           {/* === 5. CONSENTIMIENTO === */}
          {step === 5 && (
            <div className="consentimiento-step-container">
              <h3>Consentimiento</h3> <br />  

              <div className="firma-row">
                <div className="firma-input-group">
                  <label>Nombre:<input name="consentimientoNombre" value={formData.consentimientoNombre} onChange={handleChange} /></label>
                  <label>DNI:<input name="consentimientoDni" value={formData.consentimientoDni} onChange={handleChange} /></label>
                  <label>Domicilio:<input name="consentimientoDomicilio" value={formData.consentimientoDomicilio} onChange={handleChange} /></label>
                  <label>Dr/a MP:<input name="consentimientoDr" value={formData.consentimientoDr} onChange={handleChange} /></label>
                </div>

                <div className="firma-box">
                  <label className="label-firma">Firma del Paciente:</label>
                  <div className="firma-canvas-wrapper">
                    <SignatureCanvas
                      penColor="#0a2e5c"
                      canvasProps={{ className: "firma-canvas" }}
                      ref={(ref) => { sigCanvas.current = ref; }}
                    />
                    <div className="firma-actions">
                      <button type="button" className="btn-firma-fullscreen boton-secundario" onClick={openFirmaFullscreen}>
                        Firmar en pantalla completa
                      </button>
                      <button
                        type="button"
                        className="btn-limpiar-firma"
                        onClick={() => {
                          sigCanvas.current?.clear();
                          setFormData({ ...formData, firmaBase64: "" });
                          setDirtyTabs((prev) => ({ ...prev, 5: true }));
                          setSaveFeedback(null);
                        }}
                      >
                        Limpiar firma
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* === BOTONES DE NAVEGACIÓN === */}
          <div className="historia-clinica-buttons">
            <button
              type="button"
              className="btn-ver-documento boton-secundario"
              onClick={() => setShowImpresion(true)}
              title="Imprimir o guardar la historia clínica en PDF"
            >
              Imprimir / PDF
            </button>

            {step === 1 && (
                <button type="button" className="btn-salir-historia" onClick={exitForm}>
                Salir
                </button>
            )}

            {step > 1 && (
                <button type="button" className="btn-cerrar-historia" onClick={prevStep}>
                ← Anterior
                </button>
            )}

            {step < 5 && (
                <button type="button" className="btn-cerrar-historia" onClick={handleSaveCurrentTab} disabled={saving}>
                {saving ? "Guardando..." : "Guardar pestaña"}
                </button>
            )}

            {step < 5 && (
                <button type="button" className="btn-guardar-historia" onClick={nextStep}>
                Siguiente →
                </button>
            )}

            {step === 5 && (
                <>
                <button type="button" className="btn-salir-historia" onClick={exitForm}>
                    Cancelar
                </button>
                <button type="button" className="btn-cerrar-historia" onClick={handleSaveCurrentTab} disabled={saving}>
                    {saving ? "Guardando..." : "Guardar pestaña"}
                </button>
                <button type="submit" className="btn-guardar-historia">
                    Guardar H.C. completa
                </button>
                </>
            )}
          </div>

        </form>
      </div>

      {showImpresion && createPortal(
        <HistoriaClinicaImpresion
          pacienteId={id}
          formData={formData}
          onClose={() => setShowImpresion(false)}
        />,
        document.body
      )}

      {firmaFullscreen && createPortal(
        <div className="hc-firma-fullscreen-overlay">
          <div className="hc-firma-fullscreen" role="dialog" aria-modal="true" aria-label="Firma del paciente">
            <div className="hc-firma-fullscreen-body">
              <p className="hc-firma-fullscreen-hint">Usá el dedo o un stylus para firmar en el recuadro.</p>
              <button type="button" className="hc-firma-fullscreen-clear boton-secundario" onClick={clearFirmaFullscreen}>
                Limpiar firma
              </button>
              <SignatureCanvas
                penColor="#0a2e5c"
                canvasProps={{ className: "hc-firma-fullscreen-canvas" }}
                ref={sigCanvasFullscreen}
              />
            </div>
            <div className="hc-firma-fullscreen-footer modal-historia-buttons">
              <button type="button" className="btn-cerrar" onClick={closeFirmaFullscreen}>Cerrar</button>
              <button type="button" className="btn-guardar" onClick={confirmFirmaFullscreen}>Guardar</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};